/**
 * A list of valid deployment server environments
 */
export enum ServerEnvironment {
    DEMO = 'DEMO',
    SANDBOX = 'SANDBOX',
    STAGE = 'STAGE',
    UAT = 'UAT',
    CN_UAT = 'CN_UAT',
    DHL_UAT = 'DHL_UAT',
    SES = 'SES',
    SYDNEY = 'SYDNEY',
    CANADA = 'CANADA',
    FRANKFURT = 'FRANKFURT',
    IRELAND = 'IRELAND',
    TOKYO = 'TOKYO',
    SAO_PAULO = 'SAO_PAULO',
    SINGAPORE = 'SINGAPORE',
    UK = 'UK',
    MENA = 'MENA',
    PRODUCTION = 'PRODUCTION',
    DEVELOPMENT = 'DEVELOPMENT'
}

/**
 * A list of valid CI server environments
 */
export enum ServerCIEnvironment {
    ANY = 'ANY',
    DEMO = 'Demo',
    DEVELOPMENT = 'Development',
    PRODUCTION = 'Production',
    SANDBOX = 'Sandbox',
    SES = 'SES',
    STAGE = 'Stage',
    UAT = 'UAT',
    CN_UAT = 'workload-cn-uat-account',
    DHL_UAT = 'workload-dhl-account'
}

/**
 * Describes the struture of a `ServerBaseUrl` object
 */
export interface ServerBaseUrl {
    /**
     * The base url to use for all Wise API calls
     */
    apiServer: string;
    /**
     * The base url to use for all Aurora API calls
     */
    auroraApiServer: string;

    /**
     * The base url to use for all Wise Socket calls
     */
    socketServer: string;
    auroraServer: string;

    /**
     * The corresponding Gitlab environment name
     */
    CIEnv: ServerCIEnvironment;

    /**
     * The base url to use for all Initial-vs-Actual (IvA) microservice calls
     */
    initialVsActualServer?: string;

    /**
     * The base url to use for all Equipment microservice calls
     */
    equipment?: string;

    /**
     * The base url to use for all Equipment socket calls
     */
    equipmentSocketServer?: string;

    /**
     * The deployment environment
     */
    environment?: ServerEnvironment;

    /**
     * The base url to use for all Vehicle profile microservice calls
     */
    vehicleProfile?: string;

    /**
     * The base url to use for all Route Path Geometry microservice calls
     */
    routePathGeometryServer?: string;

    /**
     * The base url to use for all Configurable Workflow microservice calls
     */
    configurableWorkflowServer?: string;

    /**
     * The base url to use for all Driver Cost service calls
     */
    driverCostServer?: string;

    /**
     * The base url to use for all Banner microservice calls
     */
    bannerService?: string;
}

/**
 * A list of valid `ServerBaseUrl` properties / keys
 */
export enum ServerBaseUrlKey {
    /**
     * The `ServerBaseUrl` property/key identifying the Wise API base url
     */
    API = 'apiServer',

    /**
     * The `ServerBaseUrl` property/key identifying the Wise API base url
     */
    AURORA_API = 'auroraApiServer',

    /**
     * The `ServerBaseUrl` property/key identifying the Wise Socket base url
     */
    SOCKET = 'socketServer',
    AURORA = 'auroraServer',

    /**
     * The `ServerBaseUrl` property/key identifying the Initial-vs-Actual (IvA) microservice base url
     */
    IVA = 'initialVsActualServer',

    /**
     * The `ServerBaseUrl` property/key identifying the Equipment microservice base url
     */
    EQUIPMENT = 'equipment',

    /**
     * The `ServerBaseUrl` property/key identifying the Equipment socket base url
     */
    EQUIPMENT_SOCKET = 'equipmentSocketServer',

    /**
     * The `ServerBaseUrl` property/key identifying the deployment environment
     */
    ENVIRONMENT = 'environment',

    /**
     * The `ServerBaseUrl` property/key identifying the CI environment
     */
    CI_ENVIRONMENT = 'CIEnv',

    /**
     * The `ServerBaseUrl` property/key identifying the Vehicle profile microservice base url
     */
    VEHICLE_PROFILE = 'vehicleProfile',

    /**
     * The `ServerBaseUrl` property/key identifying the Route path geometry microservice base url
     */
    ROUTE_PATH_GEOMETRY = 'routePathGeometryServer',

    /**
     * The `ServerBaseUrl` property/key identifying the Configurable workflow microservice base url
     */
    CONFIGURABLE_WORKFLOW = 'configurableWorkflowServer',

    /**
     * The `ServerBaseUrl` property/key identifying the Driver Cost service base url
     */
    DRIVER_COST = 'driverCostServer',

    /**
     * The `ServerBaseUrl` property/key identifying the Banner microservice base url
     */
    BANNER_SERVICE = 'bannerService'
}

const targetEnvEndpointMapping: Record<string, string> = {
    demo: 'admin.demo.wisesys.info',
    dev: 'admin.dev.wisesys.info',
    development: 'development',
    local: 'development',
    sandbox: 'sandbox',
    stage: 'admin.stage.wisesys.info',
    staging: 'admin.stage.wisesys.info',
    prod: 'enterprise.wisesystems.io',
    production: 'enterprise.wisesystems.io',
    uat: 'admin.uat.wisesys.info',
    'cn-uat': 'admin.cn-uat.wisesys.info',
    'dhl-uat': 'admin.dhl-uat.wisesys.info',
    ses: 'admin.ses.wisesys.info'
};

const localHostnames = new Set([
    'localhost',
    '127.0.0.1',
    '0.0.0.0',
    'wise-multi-client'
]);

const getEndpointForTargetEnv = (hostname: string): string => {
    if (!localHostnames.has(hostname)) return hostname;

    const targetEnv = process.env.VITE_TARGET_ENV?.trim().toLowerCase();
    if (!targetEnv) return hostname;

    return targetEnvEndpointMapping[targetEnv] || hostname;
};

/**
 * A mapping of Interstellar hostnames to various service server URLs
 */
export const endPointToServerMapping: Record<string, ServerBaseUrl> = {
    'admin.demo.wisesys.info': {
        apiServer: 'https://api.demo.wisesys.info',
        auroraApiServer: 'https://demo.aurora-v2.wisesys.info',
        initialVsActualServer:
            'https://69g552rju8.execute-api.us-east-1.amazonaws.com/demo',
        socketServer: 'wss://demo.socket.wisesys.info',
        auroraServer: 'https://demo-api.aurora-v2.wisesys.info',
        environment: ServerEnvironment.DEMO,
        CIEnv: ServerCIEnvironment.DEMO,
        vehicleProfile:
            'https://hgfk0t7kh3.execute-api.us-east-1.amazonaws.com/demo',
        routePathGeometryServer:
            'https://wnfltdovrb.execute-api.us-east-1.amazonaws.com/demo',
        configurableWorkflowServer:
            'https://zq3uoax4v9.execute-api.us-east-1.amazonaws.com/Prod',
        equipment: 'https://services-demo.us.wisesys.info',
        driverCostServer:
            'https://ydqfur71s3.execute-api.us-east-1.amazonaws.com/demo',
        bannerService:
            'https://x9xa461k9d.execute-api.us-east-1.amazonaws.com/demo'
    },
    'admin.dev.wisesys.info': {
        apiServer: 'https://wise-api.dev.wisesys.info',
        auroraApiServer: 'https://dev.aurora-v2.wisesys.info',
        equipment: 'https://services-stage.us.wisesys.info',
        socketServer: 'wss://socket.dev.wisesys.info',
        auroraServer: 'https://dev-api.aurora-v2.wisesys.info',
        equipmentSocketServer: 'wss://equipmentsocket-stage.us.wisesys.info',
        CIEnv: ServerCIEnvironment.DEVELOPMENT,
        vehicleProfile:
            'https://y2sdpvqnw0.execute-api.us-east-1.amazonaws.com/development',
        routePathGeometryServer:
            'https://0tc6grz4n9.execute-api.us-east-1.amazonaws.com/development',
        configurableWorkflowServer:
            'https://uu9d8rt95l.execute-api.us-east-1.amazonaws.com/Prod',
        driverCostServer:
            'https://qeyfz3s4nj.execute-api.us-east-1.amazonaws.com/dev',
        bannerService:
            'https://tp3ei4l6wg.execute-api.us-east-1.amazonaws.com/dev'
    },
    'admin.sandbox.wisesys.info': {
        apiServer: 'https://api.sandbox.wisesys.info',
        auroraApiServer: 'https://sandbox.aurora-v2.wisesys.info',
        equipment: 'https://services-stage.us.wisesys.info',
        socketServer: 'wss://dev.socket.wisesys.info',
        auroraServer: 'https://dev-api.aurora-v2.wisesys.info',
        equipmentSocketServer: 'wss://equipmentsocket-stage.us.wisesys.info',
        environment: ServerEnvironment.SANDBOX,
        CIEnv: ServerCIEnvironment.SANDBOX,
        vehicleProfile:
            'https://y2sdpvqnw0.execute-api.us-east-1.amazonaws.com/development',
        routePathGeometryServer:
            'https://0tc6grz4n9.execute-api.us-east-1.amazonaws.com/development',
        configurableWorkflowServer:
            'https://uu9d8rt95l.execute-api.us-east-1.amazonaws.com/Prod',
        driverCostServer:
            'https://qeyfz3s4nj.execute-api.us-east-1.amazonaws.com/dev',
        bannerService:
            'https://tp3ei4l6wg.execute-api.us-east-1.amazonaws.com/dev'
    },
    'admin.stage.wisesys.info': {
        apiServer: 'https://api.stage.wisesys.info',
        auroraApiServer: 'https://stage.aurora-v2.wisesys.info',
        initialVsActualServer:
            'https://zl45ifbuj0.execute-api.us-east-1.amazonaws.com/stage',
        equipment: 'https://services-stage.us.wisesys.info',
        socketServer: 'wss://stage.socket.wisesys.info',
        equipmentSocketServer: 'wss://equipmentsocket-stage.us.wisesys.info',
        environment: ServerEnvironment.STAGE,
        auroraServer: 'https://stage-api.aurora-v2.wisesys.info',
        CIEnv: ServerCIEnvironment.STAGE,
        vehicleProfile:
            'https://idlnziuvpk.execute-api.us-east-1.amazonaws.com/stage',
        routePathGeometryServer:
            'https://1k04l2v0j8.execute-api.us-east-1.amazonaws.com/stage',
        configurableWorkflowServer:
            'https://4wsqcxtrih.execute-api.us-east-1.amazonaws.com/Prod',
        driverCostServer:
            'https://tsh2txeqa4.execute-api.us-east-1.amazonaws.com/stage',
        bannerService:
            'https://1xndpawnvf.execute-api.us-east-1.amazonaws.com/stage'
    },
    'admin.uat.wisesys.info': {
        apiServer: 'https://api.uat.wisesys.info',
        auroraApiServer: 'https://uat.aurora-v2.wisesys.info',
        initialVsActualServer:
            'https://l8e0mz5odb.execute-api.us-east-1.amazonaws.com/uat',
        equipment: 'https://services-uat.us.wisesys.info',
        socketServer: 'wss://uat.socket.wisesys.info',
        equipmentSocketServer: 'wss://equipmentsocket-uat.us.wisesys.info',
        auroraServer: 'https://uat-api.aurora-v2.wisesys.info',
        environment: ServerEnvironment.UAT,
        CIEnv: ServerCIEnvironment.UAT,
        vehicleProfile:
            'https://z9fgl5xulh.execute-api.us-east-1.amazonaws.com/uat',
        routePathGeometryServer:
            'https://u0dlfowfi9.execute-api.us-east-1.amazonaws.com/uat',
        configurableWorkflowServer:
            'https://ksjgorzu2k.execute-api.us-east-1.amazonaws.com/Prod',
        driverCostServer:
            'https://z7axlvyzo1.execute-api.us-east-1.amazonaws.com/uat',
        bannerService:
            'https://vv908w5jte.execute-api.us-east-1.amazonaws.com/uat'
    },
    'wise-web-multi-client-mrs.wisesys.info': {
        apiServer: 'https://api.sandbox.wisesys.info',
        auroraApiServer: 'https://sandbox.aurora-v2.wisesys.info',
        equipment: 'https://services-stage.us.wisesys.info',
        socketServer: 'wss://dev.socket.wisesys.info',
        auroraServer: 'https://dev-api.aurora-v2.wisesys.info',
        equipmentSocketServer: 'wss://equipmentsocket-stage.us.wisesys.info',
        environment: ServerEnvironment.SANDBOX,
        CIEnv: ServerCIEnvironment.SANDBOX,
        vehicleProfile:
            'https://y2sdpvqnw0.execute-api.us-east-1.amazonaws.com/development',
        routePathGeometryServer:
            'https://0tc6grz4n9.execute-api.us-east-1.amazonaws.com/development',
        configurableWorkflowServer:
            'https://uu9d8rt95l.execute-api.us-east-1.amazonaws.com/Prod',
        driverCostServer:
            'https://qeyfz3s4nj.execute-api.us-east-1.amazonaws.com/dev',
        bannerService:
            'https://tp3ei4l6wg.execute-api.us-east-1.amazonaws.com/dev'
    },
    'admin.cn-uat.wisesys.info': {
        apiServer: 'https://wise-api.cn-uat.wisesys.info',
        auroraApiServer: 'https://uat.aurora-v2.wisesys.info',
        initialVsActualServer:
            'https://l8e0mz5odb.execute-api.us-east-1.amazonaws.com/uat',
        equipment: 'https://services-uat.ca.cn-uat.wisesys.info',
        socketServer: 'wss://socket.cn-uat.wisesys.info',
        equipmentSocketServer:
            'wss://equipmentsocket-uat.ca.cn-uat.wisesys.info',
        auroraServer: '',
        environment: ServerEnvironment.CN_UAT,
        CIEnv: ServerCIEnvironment.CN_UAT,
        vehicleProfile:
            'https://z9fgl5xulh.execute-api.us-east-1.amazonaws.com/uat',
        routePathGeometryServer:
            'https://u0dlfowfi9.execute-api.us-east-1.amazonaws.com/uat',
        configurableWorkflowServer:
            'https://ksjgorzu2k.execute-api.us-east-1.amazonaws.com/Prod',
        driverCostServer: '' // TODO(CD-112): regional UAT — needs its own dcs-<region> stack (separate Cognito issuer)
    },
    'admin.dhl-uat.wisesys.info': {
        apiServer: 'https://wise-api.dhl-uat.wisesys.info',
        auroraApiServer: 'https://uat.aurora-v2.wisesys.info',
        initialVsActualServer:
            'https://g9ezx7baqc.execute-api.us-east-2.amazonaws.com/uat',
        socketServer: 'wss://socket.dhl-uat.wisesys.info',
        environment: ServerEnvironment.DHL_UAT,
        CIEnv: ServerCIEnvironment.DHL_UAT,
        auroraServer: '',
        vehicleProfile:
            'https://z9fgl5xulh.execute-api.us-east-1.amazonaws.com/uat',
        routePathGeometryServer:
            'https://u0dlfowfi9.execute-api.us-east-1.amazonaws.com/uat',
        configurableWorkflowServer:
            'https://ksjgorzu2k.execute-api.us-east-1.amazonaws.com/Prod',
        driverCostServer: '' // TODO(CD-112): regional UAT — needs its own dcs-<region> stack (separate Cognito issuer)
    },
    'admin.ses.wisesys.info': {
        apiServer: 'https://wise-api.ses.wisesys.info',
        auroraApiServer: 'https://uat.aurora-v2.wisesys.info',
        socketServer: 'wss://socket.ses.wisesys.info',
        environment: ServerEnvironment.SES,
        CIEnv: ServerCIEnvironment.SES,
        auroraServer: '',
        vehicleProfile:
            'https://z9fgl5xulh.execute-api.us-east-1.amazonaws.com/uat',
        routePathGeometryServer:
            'https://u0dlfowfi9.execute-api.us-east-1.amazonaws.com/uat',
        configurableWorkflowServer:
            'https://ksjgorzu2k.execute-api.us-east-1.amazonaws.com/Prod',
        driverCostServer: '' // TODO(CD-112): regional UAT — needs its own dcs-<region> stack (separate Cognito issuer)
    },
    'enterprise.au.wisesystems.io': {
        apiServer: 'https://api.au.wisesys.info',
        auroraApiServer: 'https://aurora-v2.wisesys.info',
        socketServer: 'wss://au.socket.wisesys.info',
        environment: ServerEnvironment.SYDNEY,
        CIEnv: ServerCIEnvironment.PRODUCTION,
        auroraServer: '',
        vehicleProfile:
            'https://6bvb2gb6gd.execute-api.ap-southeast-2.amazonaws.com/Sydney',
        routePathGeometryServer:
            'https://bys6alpaq2.execute-api.ap-southeast-2.amazonaws.com/production',
        configurableWorkflowServer:
            'https://zcqwzh2eue.execute-api.us-east-1.amazonaws.com/Prod',
        // TODO(CD-112): regional prod — Cognito differs from dcs-prod; needs own regional dcs stack
        driverCostServer: ''
    },
    'enterprise.ca.wisesystems.io': {
        apiServer: 'https://api.ca.wisesys.info',
        auroraApiServer: 'https://aurora-v2.wisesys.info',
        equipment: 'https://services-production.us.wisesys.info',
        socketServer: 'wss://ca.socket.wisesys.info',
        equipmentSocketServer: 'wss://equipmentsocket.ca.wisesys.info',
        environment: ServerEnvironment.CANADA,
        CIEnv: ServerCIEnvironment.PRODUCTION,
        auroraServer: '',
        vehicleProfile:
            'https://ybtfow4mv3.execute-api.us-east-1.amazonaws.com/production',
        routePathGeometryServer:
            'https://ol1vgxgyob.execute-api.us-east-1.amazonaws.com/production',
        configurableWorkflowServer:
            'https://zcqwzh2eue.execute-api.us-east-1.amazonaws.com/Prod',
        driverCostServer:
            'https://lto7uvr6z9.execute-api.us-east-1.amazonaws.com/prod'
    },
    'enterprise.ce.wisesystems.io': {
        apiServer: 'https://api.ce.wisesys.info',
        auroraApiServer: 'https://aurora-v2.wisesys.info',
        socketServer: 'wss://ce.socket.wisesys.info',
        auroraServer: '',
        environment: ServerEnvironment.FRANKFURT,
        CIEnv: ServerCIEnvironment.PRODUCTION,
        vehicleProfile:
            'https://lk8db1jmye.execute-api.eu-west-2.amazonaws.com/London',
        routePathGeometryServer:
            'https://axcf5rwtva.execute-api.eu-west-2.amazonaws.com/production',
        configurableWorkflowServer:
            'https://zcqwzh2eue.execute-api.us-east-1.amazonaws.com/Prod',
        // TODO(CD-112): regional prod — Cognito differs from dcs-prod; needs own regional dcs stack
        driverCostServer: ''
    },
    'enterprise.ie.wisesystems.io': {
        apiServer: 'https://api.ie.wisesys.info',
        auroraApiServer: 'https://aurora-v2.wisesys.info',
        socketServer: 'wss://uk.socket.wisesys.info',
        auroraServer: '',
        environment: ServerEnvironment.IRELAND,
        CIEnv: ServerCIEnvironment.PRODUCTION,
        vehicleProfile:
            'https://lk8db1jmye.execute-api.eu-west-2.amazonaws.com/London',
        routePathGeometryServer:
            'https://axcf5rwtva.execute-api.eu-west-2.amazonaws.com/production',
        configurableWorkflowServer:
            'https://zcqwzh2eue.execute-api.us-east-1.amazonaws.com/Prod',
        // TODO(CD-112): regional prod — Cognito differs from dcs-prod; needs own regional dcs stack
        driverCostServer: ''
    },
    'enterprise.jp.wisesystems.io': {
        apiServer: 'https://api.jp.wisesys.info',
        auroraApiServer: 'https://aurora-v2.wisesys.info',
        socketServer: 'wss://jp.socket.wisesys.info',
        auroraServer: '',
        environment: ServerEnvironment.TOKYO,
        CIEnv: ServerCIEnvironment.PRODUCTION,
        vehicleProfile:
            'https://1nzw6ikhc4.execute-api.ap-northeast-1.amazonaws.com/Tokyo',
        routePathGeometryServer:
            'https://0q6k5or5lc.execute-api.ap-northeast-1.amazonaws.com/production',
        configurableWorkflowServer:
            'https://zcqwzh2eue.execute-api.us-east-1.amazonaws.com/Prod',
        initialVsActualServer:
            'https://918codx572.execute-api.ap-northeast-1.amazonaws.com/production',
        // TODO(CD-112): regional prod — Cognito differs from dcs-prod; needs own regional dcs stack
        driverCostServer: ''
    },
    'enterprise.sa.wisesystems.io': {
        apiServer: 'https://api.sa.wisesys.info',
        auroraApiServer: 'https://aurora-v2.wisesys.info',
        socketServer: 'wss://sa.socket.wisesys.info',
        auroraServer: '',
        environment: ServerEnvironment.SAO_PAULO,
        CIEnv: ServerCIEnvironment.PRODUCTION,
        vehicleProfile:
            'https://ybtfow4mv3.execute-api.us-east-1.amazonaws.com/production',
        routePathGeometryServer:
            'https://ol1vgxgyob.execute-api.us-east-1.amazonaws.com/production',
        configurableWorkflowServer:
            'https://zcqwzh2eue.execute-api.us-east-1.amazonaws.com/Prod',
        driverCostServer:
            'https://lto7uvr6z9.execute-api.us-east-1.amazonaws.com/prod'
    },
    'enterprise.sg.wisesystems.io': {
        apiServer: 'https://api.sg.wisesys.info',
        auroraApiServer: 'https://aurora-v2.wisesys.info',
        socketServer: 'wss://sg.socket.wisesys.info',
        auroraServer: '',
        environment: ServerEnvironment.SINGAPORE,
        CIEnv: ServerCIEnvironment.PRODUCTION,
        vehicleProfile:
            'https://1nzw6ikhc4.execute-api.ap-northeast-1.amazonaws.com/Tokyo',
        routePathGeometryServer:
            'https://0q6k5or5lc.execute-api.ap-northeast-1.amazonaws.com/production',
        configurableWorkflowServer:
            'https://zcqwzh2eue.execute-api.us-east-1.amazonaws.com/Prod',
        initialVsActualServer:
            'https://918codx572.execute-api.ap-northeast-1.amazonaws.com/production',
        // TODO(CD-112): regional prod — Cognito differs from dcs-prod; needs own regional dcs stack
        driverCostServer: ''
    },
    'enterprise.uk.wisesystems.io': {
        apiServer: 'https://api.uk.wisesys.info',
        auroraApiServer: 'https://aurora-v2.wisesys.info',
        socketServer: 'wss://uk.socket.wisesys.info',
        auroraServer: '',
        environment: ServerEnvironment.UK,
        CIEnv: ServerCIEnvironment.PRODUCTION,
        vehicleProfile:
            'https://lk8db1jmye.execute-api.eu-west-2.amazonaws.com/London',
        routePathGeometryServer:
            'https://axcf5rwtva.execute-api.eu-west-2.amazonaws.com/production',
        configurableWorkflowServer:
            'https://zcqwzh2eue.execute-api.us-east-1.amazonaws.com/Prod',
        // TODO(CD-112): regional prod — Cognito differs from dcs-prod; needs own regional dcs stack
        driverCostServer: ''
    },
    'enterprise.mena.wisesystems.io': {
        apiServer: 'https://api.mena.wisesys.info',
        auroraApiServer: 'https://aurora-v2.wisesys.info',
        socketServer: 'wss://mena.socket.wisesys.info',
        environment: ServerEnvironment.MENA,
        CIEnv: ServerCIEnvironment.PRODUCTION,
        auroraServer: '',
        vehicleProfile:
            'https://lk8db1jmye.execute-api.eu-west-2.amazonaws.com/London',
        routePathGeometryServer:
            'https://axcf5rwtva.execute-api.eu-west-2.amazonaws.com/production',
        configurableWorkflowServer:
            'https://zcqwzh2eue.execute-api.us-east-1.amazonaws.com/Prod',
        // TODO(CD-112): regional prod — Cognito differs from dcs-prod; needs own regional dcs stack
        driverCostServer: ''
    },
    'enterprise.wisesystems.io': {
        apiServer: 'https://api.wisesys.info',
        auroraApiServer: 'https://aurora-v2.wisesys.info',
        initialVsActualServer:
            'https://p8aowjzsqh.execute-api.us-east-1.amazonaws.com/production',
        equipment: 'https://services-production.us.wisesys.info',
        equipmentSocketServer:
            'wss://equipmentsocket-production.us.wisesys.info',
        socketServer: 'wss://prod.socket.wisesys.info',
        auroraServer: 'https://api.aurora-v2.wisesys.info',
        environment: ServerEnvironment.PRODUCTION,
        CIEnv: ServerCIEnvironment.PRODUCTION,
        vehicleProfile:
            'https://ybtfow4mv3.execute-api.us-east-1.amazonaws.com/production',
        routePathGeometryServer:
            'https://ol1vgxgyob.execute-api.us-east-1.amazonaws.com/production',
        configurableWorkflowServer:
            'https://zcqwzh2eue.execute-api.us-east-1.amazonaws.com/Prod',
        driverCostServer:
            'https://lto7uvr6z9.execute-api.us-east-1.amazonaws.com/prod',
        bannerService:
            'https://az1mmkwi8l.execute-api.us-east-1.amazonaws.com/prod'
    },
    'wise-multi-client:8033': {
        apiServer: 'http://wise-api:8002',
        auroraApiServer: 'https://dev.aurora-v2.wisesys.info',
        equipment: 'https://services-stage.us.wisesys.info',
        auroraServer: 'https://dev-api.aurora-v2.wisesys.info',
        socketServer: 'ws://wise-socket:1037',
        equipmentSocketServer: 'wss://equipmentsocket-stage.us.wisesys.info',
        CIEnv: ServerCIEnvironment.DEVELOPMENT,
        vehicleProfile: 'http://vehicle-profiles-api:8020',
        routePathGeometryServer:
            'https://0tc6grz4n9.execute-api.us-east-1.amazonaws.com/development',
        configurableWorkflowServer:
            'https://uu9d8rt95l.execute-api.us-east-1.amazonaws.com/Prod',
        driverCostServer: 'http://localhost:3010',
        bannerService:
            'https://tp3ei4l6wg.execute-api.us-east-1.amazonaws.com/dev'
    },
    development: {
        apiServer: 'http://localhost:8002',
        auroraApiServer: 'https://dev.aurora-v2.wisesys.info',
        initialVsActualServer:
            'https://l8e0mz5odb.execute-api.us-east-1.amazonaws.com/uat',
        equipment: 'https://services-uat.us.wisesys.info',
        socketServer: 'ws://localhost:1037',
        equipmentSocketServer: 'wss://equipmentsocket-uat.us.wisesys.info',
        environment: ServerEnvironment.UAT,
        CIEnv: ServerCIEnvironment.UAT,
        vehicleProfile: 'http://localhost:8020',
        routePathGeometryServer:
            'https://u0dlfowfi9.execute-api.us-east-1.amazonaws.com/uat',
        configurableWorkflowServer:
            'https://ksjgorzu2k.execute-api.us-east-1.amazonaws.com/Prod',
        auroraServer: 'https://dev-api.aurora-v2.wisesys.info',
        driverCostServer: 'http://localhost:3010',
        bannerService:
            'https://tp3ei4l6wg.execute-api.us-east-1.amazonaws.com/dev'
    },
    sandbox: {
        apiServer: 'https://api.sandbox.wisesys.info',
        auroraApiServer: 'https://dev.aurora-v2.wisesys.info',
        equipment: 'https://services-stage.us.wisesys.info',
        socketServer: 'wss://dev.socket.wisesys.info',
        auroraServer: 'https://dev-api.aurora-v2.wisesys.info',
        equipmentSocketServer: 'wss://equipmentsocket-stage.us.wisesys.info',
        CIEnv: ServerCIEnvironment.SANDBOX,
        vehicleProfile:
            'https://y2sdpvqnw0.execute-api.us-east-1.amazonaws.com/development',
        routePathGeometryServer:
            'https://0tc6grz4n9.execute-api.us-east-1.amazonaws.com/development',
        configurableWorkflowServer:
            'https://uu9d8rt95l.execute-api.us-east-1.amazonaws.com/Prod',
        driverCostServer:
            'https://qeyfz3s4nj.execute-api.us-east-1.amazonaws.com/dev',
        bannerService:
            'https://tp3ei4l6wg.execute-api.us-east-1.amazonaws.com/dev'
    }
};

/**
 * Retrieves the base url for the target server
 *
 * @param {ServerBaseUrlKey} server - the target server key to retrieve from the mapping
 * @returns {string} - Returns the URL or empty string if not found
 */
export const getURL = (server: ServerBaseUrlKey): string => {
    const { hostname } = window.location;
    // `VITE_TARGET_ENV` redirects the banner and environment lookups on a
    // local hostname: the environment key drives the DoorDash integrations
    // base url, and without it a stage-targeted dev server quotes against UAT
    // and the dispatch comparison silently fails locally. API, socket, and
    // driver-cost stay local — the driver-cost gateway's CORS allowlist only
    // admits the deployed origin, so local runs go through the :3010 shim.
    const targetEnvAwareKeys = new Set([
        ServerBaseUrlKey.BANNER_SERVICE,
        ServerBaseUrlKey.ENVIRONMENT
    ]);
    const endPoint = targetEnvAwareKeys.has(server)
        ? getEndpointForTargetEnv(hostname)
        : hostname;
    const isSandbox =
        endPoint.match(/.*\.wisesys\.info/) ||
        endPoint.match(/^wisesystems\.gitlab\.io\/.*/);
    const {
        development: { [server]: devServer },
        sandbox: { [server]: sandboxServer }
    } = endPointToServerMapping;
    const envConfig =
        endPointToServerMapping[
            endPoint as keyof typeof endPointToServerMapping
        ] || {};
    const hasEnvServer = Object.prototype.hasOwnProperty.call(
        envConfig,
        server
    );
    const envServer = envConfig[server];

    if (hasEnvServer) {
        return envServer || '';
    }

    // Return empty string instead of undefined to prevent runtime errors
    return (isSandbox && sandboxServer) || devServer || '';
};
